import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { User } from '../../../api/models/user';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

export interface NewUserData {
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
  role: string;
  roleId?: number;
}

export interface DialogResult {
  mode: 'existing' | 'new';
  existingUser?: User;
  newUser?: NewUserData;
}

@Component({
  selector: 'app-user-form-dialog',
  templateUrl: './user-form-dialog.component.html',
  styleUrls: ['./user-form-dialog.component.css']
})
export class UserFormDialogComponent implements OnInit {
  // mode can be 'existing' or 'new'
  mode: 'existing' | 'new' = 'existing';
  
  // New user form data
  user: NewUserData = {
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    role: 'User'
  };

  // List of roles for new user
  roles: string[] = ['Admin', 'Project Manager', 'User'];

  // For existing user autocomplete
  userCtrl = new FormControl();
  filteredUsers: Observable<User[]>;
  selectedExistingUser: User = null;

  constructor(
    public dialogRef: MatDialogRef<UserFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { existingUsers: User[] }
  ) {}

  ngOnInit() {
    this.filteredUsers = this.userCtrl.valueChanges.pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : value.email),
      map(search => search ? this._filter(search) : this.data.existingUsers.slice())
    );
  }

  private _filter(search: string): User[] {
    const filterValue = search.toLowerCase();
    return this.data.existingUsers.filter(user =>
      user.email.toLowerCase().includes(filterValue)
    );
  }

  displayUser(user: User): string {
    return user ? `${user.email}` : '';
  }

  onUserSelected(user: User) {
    this.selectedExistingUser = user;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.mode === 'new') {
      if (this.user.password !== this.user.confirmPassword) {
        alert('Passwords do not match!');
        return;
      }
      
      // Map role string to roleId: Admin -> 1, Project Manager -> 2, User -> 3
      const roleMapping = {
        'Admin': 1,
        'Project Manager': 2,
        'User': 3
      };
      
      const newUserData: NewUserData = {
        email: this.user.email,
        username: this.user.username,
        password: this.user.password,
        confirmPassword: this.user.confirmPassword,
        role: this.user.role,
        roleId: roleMapping[this.user.role]  // Set the roleId based on the mapping.
      };
      
      const result: DialogResult = { mode: 'new', newUser: newUserData };
      this.dialogRef.close(result);
    } else {
      if (!this.selectedExistingUser) {
        alert('Please select an existing user.');
        return;
      }
      const result: DialogResult = { mode: 'existing', existingUser: this.selectedExistingUser };
      this.dialogRef.close(result);
    }
  }
}